import { memo, type PropsWithChildren, type ReactNode } from 'react';
import { type AriaButtonProps } from 'react-aria';
import cn from 'classnames';
import { Text, VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { makeModifierClassname } from '../../../../lib/makeModifierClassname';
import styles from './DrawerButton.module.css';

interface DrawerButtonProps extends PropsWithChildren, Omit<AriaButtonProps, 'type'>, TestProps {
    type?: 'primary' | 'secondary';
    isDisabled?: boolean;
    subtitle?: string;
    className?: string;
    onClick?: VoidFunction;
    addonStart?: ReactNode;
    addonEnd?: ReactNode;
}

const buttonModifier = makeModifierClassname(styles, 'button');

export const DrawerButton = memo<DrawerButtonProps>(function DrawerButton({
    'data-testid': dataTestId = 'DrawerButton',
    children,
    type = 'primary',
    isDisabled = false,
    subtitle,
    className,
    onClick,
    'aria-label': ariaLabel = 'Button',
    addonStart,
    addonEnd,
    ...props
}) {
    const ElementTag = props.href ? 'a' : 'button';

    const showAddon = Boolean(addonStart || addonEnd);

    return (
        <ElementTag
            {...props}
            data-testid={dataTestId}
            className={cn(
                buttonModifier.className,
                className,
                buttonModifier('variant', type),
                buttonModifier('disabled', isDisabled),
                buttonModifier('showAddon', showAddon)
            )}
            aria-label={ariaLabel}
            onClick={onClick}
        >
            {showAddon && <VStack className={styles.addon}>{addonStart}</VStack>}

            <VStack as="span" gap="2" alignItems="center">
                <Text className={styles.title}>{children}</Text>

                {subtitle && (
                    <Text className={styles.subtitle} size="mc">
                        {subtitle}
                    </Text>
                )}
            </VStack>

            {showAddon && <VStack className={styles.addon}>{addonEnd}</VStack>}
        </ElementTag>
    );
});
